import { useEffect, useState, useRef } from "react";
import {
  Space,
  Table,
  Tooltip,
  Pagination,
  Button,
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
  Popconfirm,
  Alert,
} from "antd";
import type { TableProps } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  HistoryOutlined,
  CloseOutlined,
  SaveOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";
import BaseService from "../../../service/base.service";
import ChangelogsManagement from "../../../models/changelogsManagement/changelogsManagement";
import changelogsManagementDetails from "../../../models/changelogsManagement/changelogsManagementDetails";
import PaginationResponse from "../../../models/paginationResponse";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { useAppDispatch } from "../../../hooks/redux-hooks";
import dayjs, { Dayjs } from "dayjs";
import notify from "../../../components/common/notificationHelper";

interface FormValues {
  id: number;
  version: string;
  date: Dayjs | null;
  changes: {
    type: "feature" | "improvement" | "bugfix" | "internal" | "other";
    description: string;
  }[];
}

const PAGE_SIZE = 10;
const { Option } = Select;

const ChangelogsManagementPage = () => {
  const isMobile = useIsMobile();
  const [listChangelogs, setListChangelogs] = useState<ChangelogsManagement[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [searchInput, setSearchInput] = useState<string>("");
  const [searchText, setSearchText] = useState<string>("");
  const [form] = Form.useForm<FormValues>();
  const horizontalPadding = isMobile ? 12 : 24;
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [tableScrollY, setTableScrollY] = useState<number>(400);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const dispatch = useAppDispatch();

  useEffect(() => {
    const updateTableHeight = () => {
      if (tableContainerRef.current) {
        const containerHeight = tableContainerRef.current.clientHeight;
        const offset = isMobile ? 72 : 100;
        setTableScrollY(Math.max(containerHeight - offset, 200));
      }
    };
    updateTableHeight();
    window.addEventListener("resize", updateTableHeight);
    return () => window.removeEventListener("resize", updateTableHeight);
  }, [isMobile]);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchText(searchInput);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchInput]);

  useEffect(() => {
    handleGetAllChangelogs(currentPage, searchText);
  }, [currentPage, searchText]);

  useEffect(() => {
    if (isModalOpen) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (contentRef.current) {
            contentRef.current.scrollTop = 0;
          } else {
            const modalBody = document.querySelector(".ant-modal-body") as HTMLElement | null;
            if (modalBody) modalBody.scrollTop = 0;
          }
        }, 0);
      });
    }
  }, [isModalOpen]);

  const handleGetAllChangelogs = async (page: number, search: string) => {
    setLoading(true);
    try {
      let url = `/changelogs/paginated?page=${page - 1}&size=${PAGE_SIZE}`;
      if (search && search.trim() !== "") {
        url += `&search=${encodeURIComponent(search.trim())}`;
      }
      const rp = await BaseService.get<PaginationResponse<ChangelogsManagement>>(url, null);
      if (rp.Status) {
        const paginationResponse = rp.Data || [];
        const changelogsList = paginationResponse.data.map(
          (c: any) => new ChangelogsManagement(c.id, c.version, c.date)
        );
        setListChangelogs(changelogsList);
        setTotalElements(paginationResponse.totalElements);
        setCurrentPage(page);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteChangelog = (changelog: ChangelogsManagement) => {
    BaseService.delete("/changelogs/", changelog.id).then((rp) => {
      if (rp.Status) {
        notify({
          title: "Changelog deleted successfully",
          description: `${changelog.version} has been deleted.`,
          variant: "info",
        });
        handleGetAllChangelogs(currentPage, searchText);
      } else {
        notify({
          title: "Error deleting changelog",
          description: `Failed to delete ${changelog.version}.`,
          variant: "error",
        });
      }
    });
  };

  const handleAddAndEditChangelog = (values: FormValues) => {
    const payload = {
      ...values,
      date: values.date ? values.date.format("DD.MM.YYYY") : null,
    };

    const request = isEdit
      ? BaseService.update("/changelogs/", payload.id, payload)
      : BaseService.create("/changelogs", payload);

    request.then((rp) => {
      if (rp.Status) {
        notify({
          title: isEdit
            ? "Changelog updated successfully"
            : "Changelog added successfully",
          description: `${payload.version} has been ${isEdit ? "updated" : "added"
            }.`,
          variant: "success",
        });
        handleGetAllChangelogs(currentPage, searchText);
      } else {
        notify({
          title: "Error saving changelog",
          description: `Failed to save ${payload.version}.`,
          variant: "error",
        });
      }
    });

    setIsModalOpen(false);
  };

  const handleCancel = () => setIsModalOpen(false);
  const handleAdd = () => {
    setIsEdit(false);
    form.resetFields();
    setIsModalOpen(true);
  };

  const handleEdit = async (changelog: ChangelogsManagement) => {
    try {
      const rp = await BaseService.get<changelogsManagementDetails>("/changelogs/", changelog.id);
      if (rp.Status) {
        setIsEdit(true);
        const data = rp.Data;
        form.setFieldsValue({
          ...data,
          date: dayjs(data.date, "DD.MM.YYYY"),
        });
        setIsModalOpen(true);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handlePageChange = (page: number) => setCurrentPage(page);

  const renderActions = (record: ChangelogsManagement) => (
    <Space size="middle">
      <Tooltip title={`Edit ${record.version}`}>
        <EditOutlined
          style={{ cursor: "pointer" }}
          onClick={() => handleEdit(record)}
        />
      </Tooltip>
      <Popconfirm
        onConfirm={() => handleDeleteChangelog(record)}
        okText="Yes"
        cancelText="No"
        okButtonProps={{ danger: true }}
        title={
          <div>
            Are you sure you want to delete version{" "}
            <strong>{record.version}</strong>?
          </div>
        }
      >
        <Tooltip title={`Delete ${record.version}`}>
          <DeleteOutlined style={{ color: "#ff4d4f", cursor: "pointer" }} />
        </Tooltip>
      </Popconfirm>
    </Space>
  );

  const columns: TableProps<ChangelogsManagement>["columns"] = isMobile
    ? [
        {
          title: "Changelog",
          key: "changelog",
          render: (_: unknown, record: ChangelogsManagement) => (
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: "#1f2937" }}>
                v{record.version}
              </div>
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                {record.date}
              </div>
            </div>
          ),
        },
        {
          title: "",
          key: "action",
          width: 72,
          align: "right",
          render: (_: unknown, record: ChangelogsManagement) =>
            renderActions(record),
        },
      ]
    : [
        { title: "ID", dataIndex: "id", key: "id" },
        { title: "Date", dataIndex: "date", key: "date" },
        { title: "Version", dataIndex: "version", key: "version" },
        {
          title: "Action",
          key: "id",
          dataIndex: "id",
          render: (_: unknown, record: ChangelogsManagement) =>
            renderActions(record),
        },
      ];

  return (
    <>
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          height: "100%",
          padding: isMobile ? "8px 0 16px" : "16px 16px 24px",
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        <div style={{ marginBottom: isMobile ? 12 : 24, flexShrink: 0 }}>
          <Alert
            message={
              isMobile
                ? "Manage changelog versions and release updates."
                : "Manage changelog versions and release updates from here."
            }
            type="info"
            showIcon={!isMobile}
            style={{
              background: "#f0f7ff",
              borderColor: "#d0e4ff",
              color: "#2c5282",
              borderRadius: 8,
              padding: isMobile ? "8px 12px" : "10px 14px",
              fontSize: isMobile ? 13 : 14,
            }}
          />
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "stretch" : "center",
            justifyContent: "space-between",
            gap: isMobile ? 10 : 12,
            background: "#fff",
            border: "1px solid #f0f0f0",
            borderRadius: 8,
            padding: isMobile ? "10px 12px" : "12px 16px",
            marginBottom: isMobile ? 12 : 16,
            boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
            flexShrink: 0,
          }}
        >
          <Input
            placeholder="Search by version, description or type"
            allowClear
            style={{ width: isMobile ? "100%" : 320 }}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />

          <Button
            type="primary"
            onClick={handleAdd}
            icon={<PlusOutlined />}
            block={isMobile}
            style={{
              borderRadius: 6,
              padding: "6px 18px",
              fontWeight: 500,
            }}
          >
            Add Changelog
          </Button>
        </div>

        <div
          ref={tableContainerRef}
          style={{
            flex: 1,
            minHeight: 0,
            background: "#fff",
            border: "1px solid #f0f0f0",
            borderRadius: 8,
            boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
            padding: isMobile ? 10 : 16,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Table
            columns={columns}
            dataSource={listChangelogs}
            pagination={false}
            scroll={{ y: tableScrollY, x: isMobile ? undefined : true }}
            sticky
            bordered={false}
            loading={loading}
            locale={{ emptyText: "No changelogs found" }}
            rowKey="id"
            size={isMobile ? "small" : "middle"}
          />
        </div>

        <div
          style={{
            marginTop: isMobile ? 12 : 20,
            display: "flex",
            justifyContent: "center",
            paddingBottom: isMobile ? 8 : 24,
            flexShrink: 0,
          }}
        >
          <Pagination
            current={currentPage}
            pageSize={PAGE_SIZE}
            total={totalElements}
            onChange={handlePageChange}
            showSizeChanger={false}
            size={isMobile ? "small" : "default"}
            simple={isMobile}
          />
        </div>
      </div>

      {/* Modal */}
      <Modal
        title={
          <div
            style={{
              textAlign: "center",
              fontWeight: 600,
              fontSize: isMobile ? "1.2rem" : "1.5rem",
              color: "#1f1f1f",
              borderBottom: "1px solid #f0f0f0",
              paddingBottom: 12,
            }}
          >
            {isEdit ? (
              <>
                <EditOutlined style={{ color: "#1677ff", marginRight: 8 }} />
                Edit Changelog
              </>
            ) : (
              <>
                <HistoryOutlined style={{ color: "#52c41a", marginRight: 8 }} />
                Add Changelog
              </>
            )}
            <div
              style={{
                fontSize: isMobile ? 13 : 14,
                fontWeight: 400,
                color: "#6b7280",
                marginTop: 4,
              }}
            >
              {isEdit
                ? "Update changelog information below"
                : "Fill in the details to add a new changelog"}
            </div>
          </div>
        }
        centered
        open={isModalOpen}
        footer={null}
        width={isMobile ? "calc(100vw - 24px)" : "60vw"}
        confirmLoading={loading}
        onCancel={handleCancel}
        styles={{
          body: {
            maxHeight: isMobile ? "75vh" : "80vh",
            overflowY: "auto",
            position: "relative",
            padding: isMobile ? "12px 12px 0" : undefined,
          },
        }}
      >
        <div
          ref={contentRef}
          style={{
            maxHeight: isMobile ? "55vh" : "65vh",
            overflowY: "auto",
            paddingRight: 8,
            WebkitOverflowScrolling: "touch",
          }}
        >
          <Form<FormValues>
            form={form}
            name="validateOnly"
            layout="vertical"
            autoComplete="off"
            onFinish={handleAddAndEditChangelog}
            style={{ padding: `16px ${horizontalPadding}px 0` }}
          >
            {isEdit && (
              <Form.Item name="id" label="Changelog ID">
                <Input disabled />
              </Form.Item>
            )}
            <Form.Item name="version" label="Version" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="date" label="Date" rules={[{ required: true }]}>
              <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} />
            </Form.Item>

            <Form.List name="changes">
              {(fields, { add, remove }) => (
                <>
                  <label><strong>Changes</strong></label>
                  {fields.map(({ key, name, ...restField }) => (
                    <div
                      key={key}
                      style={{
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        alignItems: isMobile ? "stretch" : "baseline",
                        gap: isMobile ? 8 : 8,
                        marginBottom: 8,
                      }}
                    >
                      <Form.Item
                        {...restField}
                        name={[name, "type"]}
                        rules={[{ required: true, message: "Missing type" }]}
                        style={{ marginBottom: isMobile ? 0 : undefined, flexShrink: 0 }}
                      >
                        <Select placeholder="Type" style={{ width: isMobile ? "100%" : 120 }}>
                          <Option value="feature">Feature</Option>
                          <Option value="improvement">Improvement</Option>
                          <Option value="bugfix">Bug Fix</Option>
                          <Option value="internal">Internal</Option>
                          <Option value="other">Other</Option>
                        </Select>
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, "description"]}
                        rules={[{ required: true, message: "Missing description" }]}
                        style={{ marginBottom: isMobile ? 0 : undefined, flex: 1 }}
                      >
                        <Input placeholder="Description" style={{ width: "100%" }} />
                      </Form.Item>
                      <MinusCircleOutlined
                        onClick={() => remove(name)}
                        style={{ alignSelf: isMobile ? "flex-end" : "center", paddingBottom: isMobile ? 8 : 0 }}
                      />
                    </div>
                  ))}
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                      Add Change
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Form>
        </div>

        {/* Modal Footer */}
        <div
          style={{
            position: "sticky",
            bottom: 0,
            background: "#fff",
            padding: isMobile ? "12px 8px" : "16px 24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
            borderTop: "1px solid #f0f0f0",
            boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.04)",
            zIndex: 10,
          }}
        >
          <Button onClick={handleCancel} danger icon={<CloseOutlined />}>
            Cancel
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={() => form.submit()}
            loading={loading}
          >
            {isEdit ? "Update" : "Save"}
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default ChangelogsManagementPage;
